import React from 'react';
import { useNavigate } from 'react-router-dom';

type HomeProps = {
  user: any;
  loginWithRedirect: () => void;
  getAccessTokenSilently: (options?: any) => Promise<string>;
};

const Home: React.FC<HomeProps> = ({ user, loginWithRedirect, getAccessTokenSilently }) => {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 768;
  const [workflowView, setWorkflowView] = React.useState<'student' | 'tutor'>('tutor');

  const sectionEyebrowStyle: React.CSSProperties = {
    margin: '0 0 8px 0',
    fontSize: isMobile ? '0.72rem' : '0.76rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#9ca3af',
    textAlign: 'left'
  };

  const sectionHeadingStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.65rem' : isTablet ? '2rem' : '2.4rem',
    fontWeight: 700,
    textAlign: 'left',
    color: '#111827',
    lineHeight: 1.2
  };

  return (
    <div style={{ width: '100%', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: isMobile ? '40px 16px 60px' : isTablet ? '50px 20px 70px' : '60px 20px 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        marginTop: '-8px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.16)',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: 999,
            padding: isMobile ? '6px 12px' : '7px 14px',
            fontSize: isMobile ? '0.68rem' : '0.74rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: isMobile ? 10 : 14
          }}>
            EXAM PREP + TUTOR PLATFORM
          </div>
          <h1 style={{
            fontSize: isMobile ? '1.8rem' : isTablet ? '2.5rem' : '3.5rem',
            fontWeight: 800,
            marginBottom: isMobile ? 12 : 20,
            letterSpacing: '-1px',
            lineHeight: 1.2
          }}>
            Learn, Practice, and Run Classes in One Place
          </h1>
          <p style={{
            fontSize: isMobile ? '0.9rem' : isTablet ? '1.1rem' : '1.3rem',
            marginBottom: isMobile ? 20 : 30,
            opacity: 0.95,
            maxWidth: 700,
            margin: `0 auto ${isMobile ? '20px' : '30px'}`,
            lineHeight: 1.5
          }}>
            Exam-style questions for students plus assignment, homework, and progress workflows for tutors.
          </p>
          <p style={{
            fontSize: isMobile ? '0.82rem' : '0.95rem',
            margin: `0 auto ${isMobile ? '16px' : '22px'}`,
            opacity: 0.9,
            maxWidth: 700,
            fontWeight: 600
          }}>
            Trusted by families and tutoring teams across Australia with 1000+ practice questions.
          </p>
          <div style={{
            display: 'flex',
            gap: isMobile ? 8 : 16,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.href = '/courses'}
              style={{
                background: '#fff',
                color: '#667eea',
                border: 'none',
                padding: isMobile ? '10px 20px' : '14px 40px',
                fontSize: isMobile ? '0.85rem' : '1rem',
                fontWeight: 600,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                flex: isMobile ? '1 1 calc(50% - 4px)' : 'auto',
                minWidth: isMobile ? 'auto' : '140px'
              }}
              onMouseOver={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
              }}
            >
              Start Learning
            </button>
            <button
              onClick={() => window.location.href = '/assignments'}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '2px solid white',
                padding: isMobile ? '8px 18px' : '12px 38px',
                fontSize: isMobile ? '0.85rem' : '1rem',
                fontWeight: 600,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                flex: isMobile ? '1 1 calc(50% - 4px)' : 'auto',
                minWidth: isMobile ? 'auto' : '140px'
              }}
              onMouseOver={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }}
            >
              Tutor Workspace
            </button>
            <button
              onClick={() => window.location.href = '/practice-tests'}
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.7)',
                padding: isMobile ? '8px 18px' : '12px 30px',
                fontSize: isMobile ? '0.84rem' : '0.98rem',
                fontWeight: 600,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                flex: isMobile ? '1 1 100%' : 'auto',
                minWidth: isMobile ? 'auto' : '140px'
              }}
              onMouseOver={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Mock Tests
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        background: '#f8f9fa',
        padding: isMobile ? '28px 16px' : isTablet ? '36px 20px' : '44px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 0,
            width: '100%'
          }}>
            <div style={{
              textAlign: 'center',
              border: '1px solid #e5e7eb',
              borderLeft: 'none',
              borderRadius: 0,
              padding: isMobile ? '12px 8px' : '16px 10px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.55rem' : isTablet ? '1.9rem' : '2.2rem',
                fontWeight: 800,
                color: '#667eea',
                marginBottom: 6
              }}>
                1000+
              </div>
              <p style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#666', fontWeight: 500 }}>Practice Questions</p>
            </div>
            <div style={{
              textAlign: 'center',
              border: '1px solid #e5e7eb',
              borderLeft: 'none',
              borderRadius: 0,
              padding: isMobile ? '12px 8px' : '16px 10px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.55rem' : isTablet ? '1.9rem' : '2.2rem',
                fontWeight: 800,
                color: '#667eea',
                marginBottom: 6
              }}>
                50+
              </div>
              <p style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#666', fontWeight: 500 }}>Full Practice Tests</p>
            </div>
            <div style={{
              textAlign: 'center',
              border: '1px solid #e5e7eb',
              borderLeft: 'none',
              borderRight: 'none',
              borderRadius: 0,
              padding: isMobile ? '12px 8px' : '16px 10px'
            }}>
              <div style={{
                fontSize: isMobile ? '1.55rem' : isTablet ? '1.9rem' : '2.2rem',
                fontWeight: 800,
                color: '#667eea',
                marginBottom: 6
              }}>
                95%
              </div>
              <p style={{ fontSize: isMobile ? '0.75rem' : '0.9rem', color: '#666', fontWeight: 500 }}>Success Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tutor Spotlight Section */}
      <section style={{
        padding: isMobile ? '34px 16px' : isTablet ? '42px 20px' : '52px 20px',
        background: '#ffffff'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={sectionEyebrowStyle}>
            For tutors
          </p>
          <h2 style={{ ...sectionHeadingStyle, margin: '0 0 10px 0' }}>
            Run your tutoring workflow with less admin
          </h2>
          <p style={{
            margin: '0 0 24px 0',
            textAlign: 'left',
            color: '#6b7280',
            fontSize: isMobile ? '0.95rem' : '1rem',
            lineHeight: 1.5
          }}>
            Create assignments, publish homework, and monitor student progress from a single dashboard.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? 12 : 16,
            marginBottom: 18
          }}>
            {[
              {
                icon: '📝',
                title: 'Assignments & Homework',
                desc: 'Assign work quickly and keep class tasks organized by topic and due date.'
              },
              {
                icon: '📈',
                title: 'Progress Visibility',
                desc: 'Track attempts, completion, and performance insights to guide next lessons.'
              },
              {
                icon: '🏫',
                title: 'Organization Ready',
                desc: 'Support tutoring centers and independent educators with shared workflows.'
              }
            ].map((item) => (
              <div
                key={item.title}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: isMobile ? '14px 14px' : '16px 16px',
                  background: '#f9fafb',
                  textAlign: 'left'
                }}
              >
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  background: '#e0e7ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  marginBottom: 10
                }}>
                  {item.icon}
                </div>
                <h3 style={{
                  margin: '0 0 6px 0',
                  fontSize: isMobile ? '1rem' : '1.05rem',
                  lineHeight: 1.3,
                  fontWeight: 700,
                  color: '#111827'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: isMobile ? '0.9rem' : '0.94rem',
                  lineHeight: 1.45,
                  color: '#4b5563'
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <button
              onClick={() => window.location.href = '/assignments'}
              style={{
                background: '#4f46e5',
                color: '#ffffff',
                border: 'none',
                padding: isMobile ? '10px 16px' : '12px 20px',
                borderRadius: 8,
                fontSize: isMobile ? '0.88rem' : '0.95rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Open Tutor Workspace
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section style={{
        padding: isMobile ? '40px 16px' : isTablet ? '50px 20px' : '60px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {(() => {
            const workflowSteps = workflowView === 'tutor'
              ? [
                  {
                    number: '1',
                    title: 'Set up your tutoring organization',
                    description: 'Create your organization or join an existing one to manage classes in a shared workspace.',
                    to: '/organization-setup'
                  },
                  {
                    number: '2',
                    title: 'Publish homework and assignments',
                    description: 'Assign targeted tasks and keep student practice structured by topic and due date.',
                    to: '/assignments'
                  },
                  {
                    number: '3',
                    title: 'Track outcomes and improve lessons',
                    description: 'Use analytics and attempt data to guide interventions and plan the next class.',
                    to: '/analytics'
                  }
                ]
              : [
                  {
                    number: '1',
                    title: 'Pick your course',
                    description: 'Choose the exam you’re preparing for — selective school, CBSE, or CAT.',
                    to: '/courses'
                  },
                  {
                    number: '2',
                    title: 'Practice by topic',
                    description: 'Drill specific subjects and track which areas need more work.',
                    to: '/practice?courseId=1&categoryId=25'
                  },
                  {
                    number: '3',
                    title: 'Take a full mock test',
                    description: 'Simulate real exam conditions with timed full-length tests.',
                    to: '/practice-tests'
                  }
                ];

            return (
          <div style={{
            display: isTablet ? 'block' : 'grid',
            gridTemplateColumns: isTablet ? undefined : 'minmax(240px, 340px) 1fr',
            alignItems: 'start',
            gap: isTablet ? 0 : 24
          }}>
          <div style={{ textAlign: 'left', marginBottom: isTablet ? (isMobile ? 18 : 22) : 0 }}>
            <p style={sectionEyebrowStyle}>
              How it works
            </p>
            <h2 style={{ ...sectionHeadingStyle, margin: 0 }}>
              {workflowView === 'tutor' ? 'Run your tutoring flow in 3 steps' : 'Get started in 3 steps'}
            </h2>
            <button
              type="button"
              onClick={() => navigate('/how-it-works')}
              style={{
                marginTop: 10,
                border: 'none',
                background: 'transparent',
                padding: 0,
                color: '#4f46e5',
                fontWeight: 700,
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                cursor: 'pointer'
              }}
            >
              View full How It Works page ›
            </button>
            <div style={{
              marginTop: 14,
              display: 'inline-flex',
              background: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: 10,
              padding: 3,
              gap: 3
            }}>
              <button
                type="button"
                onClick={() => setWorkflowView('student')}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: isMobile ? '7px 10px' : '8px 12px',
                  fontSize: isMobile ? '0.78rem' : '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: workflowView === 'student' ? '#4f46e5' : 'transparent',
                  color: workflowView === 'student' ? '#fff' : '#4338ca'
                }}
              >
                Students
              </button>
              <button
                type="button"
                onClick={() => setWorkflowView('tutor')}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: isMobile ? '7px 10px' : '8px 12px',
                  fontSize: isMobile ? '0.78rem' : '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: workflowView === 'tutor' ? '#4f46e5' : 'transparent',
                  color: workflowView === 'tutor' ? '#fff' : '#4338ca'
                }}
              >
                Tutors
              </button>
            </div>
          </div>

          <div style={{
            border: '1px solid #d1d5db',
            borderRadius: 14,
            background: '#fff',
            overflow: 'hidden'
          }}>
          {workflowSteps.map((step, index) => (
            <div
              key={step.number}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: isMobile ? 12 : 14,
                padding: isMobile ? '14px 14px' : '18px 20px',
                borderBottom: index < 2 ? '1px solid #e5e7eb' : 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onClick={() => window.location.href = step.to}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { window.location.href = step.to; } }}
              tabIndex={0}
              role="button"
              aria-label={step.title}
              onMouseOver={e => { (e.currentTarget as HTMLDivElement).style.background = '#f3f4f6'; }}
              onMouseOut={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
            >
              <div style={{ flexShrink: 0 }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? 28 : 30,
                  height: isMobile ? 28 : 30,
                  borderRadius: '50%',
                  background: '#111827',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: isMobile ? 13 : 14
                }}>
                  {step.number}
                </span>
              </div>

              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <h3 style={{
                  fontSize: isMobile ? '1.15rem' : '1.45rem',
                  fontWeight: 600,
                  color: '#111827',
                  margin: '0 0 6px 0',
                  lineHeight: 1.25
                }}>
                  {step.title}
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: isMobile ? '0.98rem' : '1rem',
                  color: '#4b5563',
                  lineHeight: 1.45
                }}>
                  {step.description}
                </p>
              </div>

              <span style={{
                flexShrink: 0,
                fontSize: isMobile ? 16 : 18,
                color: '#9ca3af',
                fontWeight: 700,
                lineHeight: 1
              }}>
                ›
              </span>
            </div>
          ))}
          </div>
          </div>
            );
          })()}
        </div>
      </section>

      {/* Courses Section */}
      <section style={{
        background: '#f3f4f6',
        padding: isMobile ? '50px 16px' : isTablet ? '60px 20px' : '80px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={sectionEyebrowStyle}>
            Courses
          </p>
          <h2 style={{ ...sectionHeadingStyle, marginBottom: 10 }}>
            Browse by course
          </h2>

          <p style={{
            margin: '0 0 40px 0',
            textAlign: 'left',
            color: '#6b7280',
            fontSize: isMobile ? '0.95rem' : '1rem',
            lineHeight: 1.5
          }}>
            Choose a course and start practicing with focused content.
          </p>

          {(() => {
            const courses = [
              {
                id: 1,
                name: 'Selective School Exam – Victoria (SEHS Preparation)',
                background: '#dbe7f3',
                titleColor: '#245489',
                metaColor: '#4f6f94'
              },
              {
                id: 2,
                name: 'Selective School Exam – NSW (Placement Test Preparation)',
                background: '#dfe7d3',
                titleColor: '#2f5f14',
                metaColor: '#4d6d36'
              },
              {
                id: 3,
                name: 'GATE Selective Exam – Western Australia Preparation',
                background: '#efe2cc',
                titleColor: '#8f5a10',
                metaColor: '#8d6931'
              },
              {
                id: 4,
                name: 'IGNITE Entry Test – South Australia Preparation',
                background: '#e5ddf6',
                titleColor: '#583d9b',
                metaColor: '#6f57aa'
              },
              {
                id: 5,
                name: 'Selective School Mastery – Australia (All States Preparation)',
                background: '#faeccb',
                titleColor: '#8a5a00',
                metaColor: '#8b6a2b'
              }
            ];

            if (isMobile) {
              return (
                <>
                  <div
                    className="home-courses-scroll"
                    style={{
                    display: 'flex',
                    gap: 12,
                    overflowX: 'auto',
                    paddingBottom: 6,
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    {courses.map((course, idx) => (
                      <div
                        key={idx}
                        style={{
                          flex: '0 0 44%',
                          minWidth: 138,
                          borderRadius: 12,
                          padding: '14px 12px',
                          background: course.background,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        role="button"
                        tabIndex={0}
                        onClick={() => window.location.href = '/course-details/' + course.id}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            window.location.href = '/course-details/' + course.id;
                          }
                        }}
                        onMouseOver={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.15)';
                            e.currentTarget.style.transform = 'translateY(-5px)';
                          }
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <h3 style={{
                          margin: '0 0 8px 0',
                          fontSize: '0.98rem',
                          lineHeight: 1.2,
                          fontWeight: 700,
                          color: course.titleColor
                        }}>
                          {course.name}
                        </h3>
                        <p style={{
                          margin: '0 0 8px 0',
                          fontSize: '0.85rem',
                          color: course.metaColor,
                          fontWeight: 600
                        }}>
                          Course {course.id}
                        </p>
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: course.titleColor,
                          fontWeight: 700
                        }}>
                          Open syllabus ›
                        </p>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    marginTop: 8,
                    textAlign: 'center',
                    color: '#9ca3af',
                    fontSize: '0.78rem',
                    letterSpacing: 2.5
                  }}>
                    •••
                  </div>
                </>
              );
            }

            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
                gap: 20
              }}>
                {courses.map((course, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderRadius: 14,
                      padding: '18px 16px',
                      background: course.background,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => window.location.href = '/course-details/' + course.id}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        window.location.href = '/course-details/' + course.id;
                      }
                    }}
                    onMouseOver={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-5px)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '1.1rem',
                      lineHeight: 1.2,
                      fontWeight: 700,
                      color: course.titleColor
                    }}>
                      {course.name}
                    </h3>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '0.9rem',
                      color: course.metaColor,
                      fontWeight: 600
                    }}>
                      Course {course.id}
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '0.92rem',
                      color: course.titleColor,
                      fontWeight: 700
                    }}>
                      Open syllabus ›
                    </p>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Features Section */}
      <section style={{
        padding: isMobile ? '50px 16px' : isTablet ? '60px 20px' : '80px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <p style={sectionEyebrowStyle}>
          Features
        </p>
        <h2 style={{ ...sectionHeadingStyle, marginBottom: isMobile ? 32 : 44 }}>
          Why students choose us
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: isMobile ? 10 : isTablet ? 30 : 40
        }}>
          {[
            {
              icon: '📚',
              iconBg: '#dbe7f3',
              title: 'Comprehensive Curriculum',
              desc: 'Complete coverage of all selective school entrance exam topics with expert-curated content'
            },
            {
              icon: '🎯',
              iconBg: '#dfe7d3',
              title: 'Targeted Practice',
              desc: 'Focused practice tests that mirror actual exam patterns and difficulty levels'
            },
            {
              icon: '📊',
              iconBg: '#e5ddf6',
              title: 'Performance Analytics',
              desc: 'Detailed insights into your strengths and weaknesses with personalized recommendations'
            },
            {
              icon: '⏱️',
              iconBg: '#efe2cc',
              title: 'Time Management',
              desc: 'Timed practice sessions to develop exam speed and accuracy'
            },
            {
              icon: '💡',
              iconBg: '#faeccb',
              title: 'Expert Solutions',
              desc: 'Detailed explanations for every question to deepen understanding'
            },
            {
              icon: '🏆',
              iconBg: '#e6e7f8',
              title: 'Progress Tracking',
              desc: 'Monitor your improvement journey with comprehensive statistics and reports'
            }
          ].map((feature, idx) => (
            <div
              key={idx}
              style={{
                background: '#fff',
                border: '1px solid #e8e8e8',
                borderRadius: 12,
                padding: isMobile ? 14 : isTablet ? 30 : 40,
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: isMobile ? 34 : 52,
                height: isMobile ? 34 : 52,
                borderRadius: isMobile ? 10 : 12,
                background: feature.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '1rem' : '1.7rem',
                marginBottom: isMobile ? 10 : 16
              }}>
                {feature.icon}
              </div>
              <h3 style={{
                fontSize: isMobile ? '0.95rem' : isTablet ? '1.1rem' : '1.3rem',
                fontWeight: 600,
                marginBottom: isMobile ? 6 : 12,
                color: '#222'
              }}>
                {feature.title}
              </h3>
              <p style={{
                color: '#666',
                lineHeight: isMobile ? 1.35 : 1.6,
                fontSize: isMobile ? '0.8rem' : '0.95rem',
                margin: 0
              }}>
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section style={{
        background: '#f3f4f6',
        padding: isMobile ? '44px 16px' : isTablet ? '56px 20px' : '72px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={sectionEyebrowStyle}>
            Reviews
          </p>
          <h2 style={{ ...sectionHeadingStyle, margin: '0 0 24px 0' }}>
            What parents are saying
          </h2>

          {(() => {
            const reviews = [
              {
                quote: 'My daughter went from borderline to top 5% after 6 weeks of practice here.',
                author: 'Sarah M. — Sydney'
              },
              {
                quote: 'The step-by-step explanations are incredible. He finally understands why, not just what.',
                author: 'James T. — Melbourne'
              },
              {
                quote: 'The timed tests gave us real exam confidence. The progress reports are very helpful.',
                author: 'Priya R. — Brisbane'
              }
            ];

            if (isMobile) {
              return (
                <div style={{
                  display: 'flex',
                  gap: 12,
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  paddingBottom: 6
                }}>
                  {reviews.map((review, idx) => (
                    <article
                      key={idx}
                      style={{
                        flex: '0 0 88%',
                        minWidth: 260,
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 14,
                        padding: '16px 14px'
                      }}
                    >
                      <p style={{ margin: '0 0 10px 0', color: '#d4a72c', fontSize: '0.8rem', letterSpacing: '0.06em' }}>
                        ★★★★★
                      </p>
                      <p style={{
                        margin: '0 0 12px 0',
                        color: '#111827',
                        fontSize: '0.95rem',
                        lineHeight: 1.45
                      }}>
                        “{review.quote}”
                      </p>
                      <p style={{
                        margin: 0,
                        color: '#6b7280',
                        fontSize: '0.86rem',
                        fontWeight: 600
                      }}>
                        {review.author}
                      </p>
                    </article>
                  ))}
                </div>
              );
            }

            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: 18
              }}>
                {reviews.map((review, idx) => (
                  <article
                    key={idx}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 14,
                      padding: isTablet ? '18px 16px' : '20px 18px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      if (!isMobile) {
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-5px)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <p style={{ margin: '0 0 10px 0', color: '#d4a72c', fontSize: '0.84rem', letterSpacing: '0.06em' }}>
                      ★★★★★
                    </p>
                    <p style={{
                      margin: '0 0 12px 0',
                      color: '#111827',
                      fontSize: isTablet ? '0.98rem' : '1rem',
                      lineHeight: 1.5
                    }}>
                      “{review.quote}”
                    </p>
                    <p style={{
                      margin: 0,
                      color: '#6b7280',
                      fontSize: isTablet ? '0.9rem' : '0.92rem',
                      fontWeight: 600
                    }}>
                      {review.author}
                    </p>
                  </article>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: isMobile ? '50px 16px 40px' : isTablet ? '60px 20px 50px' : '80px 20px 60px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '1.8rem' : isTablet ? '2rem' : '2.5rem',
            fontWeight: 700,
            marginBottom: 20,
            lineHeight: 1.2
          }}>
            Ready to Excel?
          </h2>
          <p style={{
            fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
            marginBottom: isMobile ? 30 : 40,
            opacity: 0.95,
            lineHeight: 1.6
          }}>
            Join thousands of students who have successfully prepared for selective school exams with ExamMasters.
          </p>
          <button
            onClick={() => window.location.href = '/courses'}
            style={{
              background: '#fff',
              color: '#667eea',
              border: 'none',
              padding: isMobile ? '12px 30px' : '16px 50px',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: 700,
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              width: isMobile ? '100%' : 'auto'
            }}
            onMouseOver={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Get Started Free
          </button>
          <p style={{
            margin: '12px 0 0 0',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            opacity: 0.9,
            fontWeight: 500
          }}>
            No credit card required
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
